"use client";

import { useState } from "react";
import { FilterBar } from "@/features/filters/components/FilterBar";
import { useMediaQuery } from "@/features/layout/useMediaQuery";
import { Search } from "@/features/search/components/Search";
import { useSidebar } from "@/hooks/useSidebar";
import { cn } from "@/lib/cn";

/** Below this the results panel and an expanded search input cannot both fit. */
const NARROW_PX = 1125;
const NARROW = `(max-width: ${NARROW_PX - 1}px)`;

/**
 * The translucent bar over the map: search on the left, filter pills beside it.
 *
 * The two share a state — while the search input is expanded the pills drop
 * their labels to icons, because on a narrow window the expanded input and a
 * dozen labelled pills don't both fit and the pills are what gives way.
 */
export function SearchAndFilters() {
  const { isOpen: isSidebarOpen, close: closeSidebar } = useSidebar();
  const isNarrow = useMediaQuery(NARROW);
  const [wantsSearch, setWantsSearch] = useState(false);

  // Expanded search is derived rather than stored, so the rule is stated once
  // and there is no effect keeping two states in step. The rule is narrow: only
  // where they'd actually collide does the panel win — the legacy version
  // collapsed the input whenever the panel opened, at any width.
  const isSearchOpen = wantsSearch && !(isSidebarOpen && isNarrow);

  const setSearchOpen = (open: boolean) => {
    setWantsSearch(open);
    // On a narrow window the input takes the panel's room rather than running
    // off-screen beside it.
    if (open && isNarrow) closeSidebar();
  };

  const surface = cn(
    "pointer-events-auto flex flex-wrap items-center bg-gray-50 bg-opacity-40",
    "backdrop-blur dark:bg-dark-color dark:bg-opacity-20"
  );

  return (
    <div className="mr-4 flex w-max min-w-0 flex-row">
      <div className={cn(surface, "search-container h-[3.5rem] rounded-bl-[20px] pl-7")}>
        <Search isOpen={isSearchOpen} setOpen={setSearchOpen} />
      </div>

      <div
        className={cn(
          surface,
          "min-h-[3.5rem] rounded-br-[20px] pr-6 max-[935px]:pr-0"
        )}
      >
        <FilterBar isCompact={isSearchOpen} />
      </div>
    </div>
  );
}
